import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Table, { THead, TH, TBody, TR, TD, Pagination } from '../../components/ui/Table';
import StatusBadge from '../../components/shared/StatusBadge';
import { formatDate } from '../../utils/formatDate';
import toast from 'react-hot-toast';

export default function ManageCertificates() {
  const [certs, setCerts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [shipModal, setShipModal] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');

  const loadCerts = async () => {
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (filterStatus) params.append('status', filterStatus);
      const { data } = await api.get(`/certificates?${params}`);
      setCerts(data.certificates);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error('Load certs error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCerts(); }, [page, filterStatus]);

  const handleShip = async () => {
    try {
      await api.patch(`/certificates/${shipModal.id}/ship`, { trackingNumber });
      toast.success('Marked as shipped');
      setShipModal(null);
      setTrackingNumber('');
      loadCerts();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDeliver = async (id) => {
    try {
      await api.patch(`/certificates/${id}/deliver`);
      toast.success('Marked as delivered');
      loadCerts();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleResend = async (id) => {
    try {
      await api.post(`/certificates/${id}/resend`);
      toast.success('Certificate resent');
    } catch (err) {
      toast.error('Failed to resend');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Manage Certificates</h1>
          <p className="text-slate-400 mt-1">Track and manage all issued certificates</p>
        </div>
        <Link to="/admin/certificates/new">
          <Button>+ Issue Certificate</Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {['', 'issued', 'shipped', 'delivered', 'activated', 'live'].map(s => (
          <button
            key={s}
            onClick={() => { setFilterStatus(s); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === s ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30' : 'text-slate-400 hover:text-slate-200 border border-navy-700 hover:border-navy-600'
            }`}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
          </button>
        ))}
      </div>

      <Table>
        <THead>
          <tr>
            <TH>License Key</TH>
            <TH>Organization</TH>
            <TH>Software</TH>
            <TH>Status</TH>
            <TH>Issued</TH>
            <TH>Actions</TH>
          </tr>
        </THead>
        <TBody>
          {certs.map(c => (
            <TR key={c.id}>
              <TD><span className="font-mono text-cyan-400 text-xs">{c.licenseKey}</span></TD>
              <TD>{c.organization?.shortName || c.organization?.name}</TD>
              <TD><span className="text-sm">{c.softwareName}</span></TD>
              <TD><StatusBadge status={c.status} /></TD>
              <TD>{formatDate(c.issuedAt)}</TD>
              <TD>
                <div className="flex gap-2">
                  {c.status === 'issued' && (
                    <button onClick={() => setShipModal(c)} className="text-xs text-cyan-400 hover:underline">Ship</button>
                  )}
                  {c.status === 'shipped' && (
                    <button onClick={() => handleDeliver(c.id)} className="text-xs text-cyan-400 hover:underline">Deliver</button>
                  )}
                  <button onClick={() => handleResend(c.id)} className="text-xs text-slate-400 hover:underline">Resend</button>
                </div>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Ship modal */}
      <Modal isOpen={!!shipModal} onClose={() => setShipModal(null)} title="Mark as Shipped">
        <p className="text-sm text-slate-400 mb-4">
          Enter the tracking number for <span className="text-slate-200 font-mono">{shipModal?.licenseKey}</span>
        </p>
        <Input
          label="Tracking Number"
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          placeholder="e.g., EMS123456789SG"
        />
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="secondary" onClick={() => setShipModal(null)}>Cancel</Button>
          <Button onClick={handleShip} disabled={!trackingNumber}>Confirm Shipment</Button>
        </div>
      </Modal>
    </div>
  );
}
